// context/UIContext.jsx
import { createContext, useContext, useState } from "react";
import React from 'react';

interface OverlayState {
  type: 'dialog' | 'drawer';
  component: string;
  title?: string;
  subtitle?: string;
  props?: any;
  actionButtons?: any[];
  hideCloseButton?: boolean;
  showBackButton?: boolean;
  onBackClick?: (() => void) | null;
}

interface UIContextType {
  openBoostBar: boolean;
  setOpenBoostBar: (open: boolean) => void;
  toggleBoostBar: () => void;
  overlay: OverlayState | null;
  showOverlay: (config: Partial<OverlayState> & { component: any }) => void;
  hideOverlay: () => void;
  setOverlayActionButtons: (buttons: any[]) => void;
  updateOverlayTitle: (title: string, subtitle?: string | null) => void;
  updateOverlayBackButton: (show: boolean, onClick?: (() => void) | null) => void;
}

export const UIContext = createContext<UIContextType>({
  // BoostBar
  openBoostBar: false,
  setOpenBoostBar: () => {},
  toggleBoostBar: () => {},

  // Dialog/Drawer (Universal Component Manager)
  overlay: null,           // { type: 'dialog' | 'drawer', component: string, props: {}, actionButtons: [] }
  showOverlay: () => {},
  hideOverlay: () => {},
  setOverlayActionButtons: () => {},
  updateOverlayTitle: () => {},
  updateOverlayBackButton: () => {},
});

interface UIProviderProps {
  children: React.ReactNode;
}

export const UIProvider: React.FC<UIProviderProps> = ({ children }) => {
  // BoostBar state
  const [openBoostBar, setOpenBoostBar] = useState(false);
  const toggleBoostBar = () => setOpenBoostBar((prev) => !prev);

  // Overlay (Drawer/Dialog/Modal) state
  const [overlay, setOverlay] = useState(null);

  const showOverlay = (config: Partial<OverlayState> & { component: any }) => {
    const { type = "dialog", component, title, subtitle, props = {}, hideCloseButton = false, showBackButton = false, onBackClick = null } = config;
    setOverlay({ type, component, title, subtitle, props, actionButtons: [], hideCloseButton, showBackButton, onBackClick });
  };

  const hideOverlay = () => setOverlay(null);

  const setOverlayActionButtons = (actionButtons) => {
    setOverlay(prev => prev ? { ...prev, actionButtons } : null);
  };

  const updateOverlayTitle = (title, subtitle = null) => {
    setOverlay(prev => prev ? { ...prev, title, subtitle } : null);
  };

  const updateOverlayBackButton = (showBackButton, onBackClick = null) => {
    setOverlay(prev => prev ? { ...prev, showBackButton, onBackClick } : null);
  };

  return (
    <UIContext.Provider
      value={{
        openBoostBar,
        setOpenBoostBar,
        toggleBoostBar,

        overlay,
        showOverlay,
        hideOverlay,
        setOverlayActionButtons,
        updateOverlayTitle,
        updateOverlayBackButton,
      }}
    >
      {children}
    </UIContext.Provider>
  );
};

export const useUI = () => useContext(UIContext);
